export const state = () => ({
  currentUser: {},
  isAuthenticated: false
});

export const mutations = {
  setUser(state, currentUser) {
    state.currentUser = { ...state.currentUser, ...currentUser };
    state.isAuthenticated = !!currentUser;
  },
  clearAuth(state) {
    state.currentUser = {};
    state.isAuthenticated = false;
  },
  setUserPermissions(state, permissions) {
    state.currentUser = { ...state.currentUser, permissions };
  },
  setFirstName(state, firstname) {
    state.currentUser = { ...state.currentUser, firstname };
  },
  setLastName(state, lastname) {
    state.currentUser = { ...state.currentUser, lastname };
  },
  setMiddleName(state, middlename) {
    state.currentUser = { ...state.currentUser, middlename };
  },
  setEmail(state, email) {
    state.currentUser = { ...state.currentUser, email };
  },
  setUserState(state, userState) {
    state.currentUser = { ...state.currentUser, state: userState };
  },
  setUserCity(state, userCity) {
    state.currentUser = { ...state.currentUser, city: userCity };
  },
  setUserCountry(state, userCountry) {
    state.currentUser = { ...state.currentUser, country: userCountry };
  }
};

export const actions = {
  async login(_, { credentials, domain }) {
    try {
      const response = await this.$travellingSDK.AuthDomain.login(
        {
          email: credentials.email,
          password: credentials.password
        },
        domain
      );

      /** If call is successful, check for errors **/
      if (!response.msg || response.type) {
        let error;
        switch (response.type) {
          case 'locked':
            error = `This account has been locked.<br/>Please <a href="/login/forgot">reset your password</a> to login.`;
            break;
          case 'password-error':
            error =
              'Password does not meet requirements. Please verify and resubmit.';
            break;
          case 'login-error':
            error =
              'Email or password does not match our records.<br/>Please verify and resubmit.';
            break;
          default:
            error = 'Login failed due to an unknown error.';
        }
        response.error = error;
      }
      return response;
    } catch (error) {
      this.$bugsnag?.notify(error, event => {
        event.addMetadata('Extra Info', {
          action: 'login',
          endpoint: '$travellingSDK.AuthDomain.login'
        });
      });
      console.error('Login failed', error);
      throw error;
    }
  },
  async fetchUser({ commit, dispatch }) {
    try {
      const response = await this.$travellingSDK.UserCurrent.get();

      commit('setUser', response);
    } catch (error) {
      this.$bugsnag?.notify(error, event => {
        event.addMetadata('Extra Info', {
          action: 'fetchUser',
          endpoint: '$travellingSDK.UserCurrent.get'
        });
      });
      console.error('Fetch user failed', error);
      dispatch('logout');
    }
  },
  async logout({ commit }) {
    try {
      await this.$travellingSDK.Auth.logout();
      if (process.server) {
        state.currentUser = {};
        state.isAuthenticated = false;
      } else {
        commit('clearAuth');
      }

      this.$router.push({ name: 'login' });
    } catch (error) {
      this.$bugsnag?.notify(error, event => {
        event.addMetadata('Extra Info', {
          action: 'logout',
          endpoint: '$travellingSDK.Auth.logout'
        });
      });
      console.error('Logout failed', error);
    }
  },
  async checkUserPermissions({ commit }) {
    let routes = [
      {
        feature: 'peopleReport',
        method: 'GET',
        route: `/permissions/product/people_reports`
      },
      {
        feature: 'emailReport',
        method: 'GET',
        route: `/permissions/product/email_reports`
      },
      {
        feature: 'addressReport',
        method: 'GET',
        route: `/permissions/product/address_reports`
      },
      {
        feature: 'phoneReport',
        method: 'GET',
        route: `/permissions/product/phone_reports`
      },
      {
        feature: 'sexOffenderReport',
        method: 'GET',
        route: `/permissions/product/sexoffender_reports`
      },
      {
        feature: 'generatePdf',
        method: 'GET',
        route: `/permissions/product/pdf_generation`
      },
      {
        feature: 'reportMonitoring',
        method: 'GET',
        route: `/permissions/product/report_monitoring`
      },
      {
        feature: 'sexOffenderReport',
        method: 'GET',
        route: `/permissions/product/sexoffender_reports`
      }
    ];
    const routesFormattedForEndpoint = [];
    routes.forEach(route => {
      routesFormattedForEndpoint.push({
        method: route.method,
        route: route.route
      });
    });
    let checkedRoutes = [];
    try {
      checkedRoutes = await this.$travellingSDK.UserCurrent.routesCheck(
        routesFormattedForEndpoint
      );
    } catch (error) {
      this.$bugsnag?.notify(error, event => {
        event.addMetadata('Extra Info', {
          action: 'checkUserPermissions',
          endpoint: '$travellingSDK.UserCurrent.routesCheck'
        });
      });
      console.error('Failed to check user permissions:', error);
    }
    let permissions = {};

    routes.forEach(route => {
      if (!checkedRoutes.length) {
        permissions[route.feature] = false;
      } else {
        const match = checkedRoutes.find(
          returnedRoute => returnedRoute.route === route.route
        );
        permissions[route.feature] = match.allowed;
      }
    });
    commit('setUserPermissions', permissions);

    return permissions;
  }
};

export const getters = {
  currentUser: state => state.currentUser,
  isAuthenticated: state => state.isAuthenticated,
  hasPeoplePermission: state => state.currentUser?.permissions?.peopleReport,
  hasEmailPermission: state => state.currentUser?.permissions?.emailReport,
  hasAddressPermission: state => state.currentUser?.permissions?.addressReport,
  hasPhonePermission: state => state.currentUser?.permissions?.phoneReport,
  hasOffenderPermission: state =>
    state.currentUser?.permissions?.sexOffenderReport,
  hasEnabledPdf: state => state.currentUser?.permissions?.generatePdf,
  hasEnabledMonitoring: state =>
    state.currentUser?.permissions?.reportMonitoring
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
